import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { AngularFirestore } from '@angular/fire/firestore';
import { AngularFireAuth } from '@angular/fire/auth';
import { AuthUser } from './auth.user';
import {
  MyActivity,
  DefaultActivity,
  MyDailyValue,
  MyDefaultActivity,
  SleepIntraday,
} from '../interfaces/activity';
import { UserProfile } from '../interfaces/user';
import { Observable } from 'rxjs';
import {
  UserSurveyInfo,
  SurveyQuestion,
  SurveyAnswer,
} from '../interfaces/survey';
import { take } from 'rxjs/operators';
import { HelperService } from './helper.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  date = this.hs.getLocalZoneISOString(new Date());

  uid;
  fireUserId;
  register: boolean;
  reg: boolean;
  title: string;
  public navigateHomeToLogin(register: boolean, title: string, reg: boolean) {
    this.register = register;
    this.title = title;
    this.reg = reg;
  }

  constructor(
    private afAuth: AngularFireAuth,
    public db: AngularFireDatabase,
    private hs: HelperService,
    private store: AngularFirestore
  ) {
    this.getAuthUser(() => { });
  }

  saveSurveyAnswerArr(
    surveyArr: Array<SurveyAnswer>,
    surveyCompletionInfo: any,
  ) {
    this.store
      .collection('survey-answers')
      .doc(this.fireUserId)
      .collection('survey')
      .doc(surveyCompletionInfo.uuid)
      .set(surveyCompletionInfo);
    const date = this.hs.getLocalZoneISOString(new Date());
    for (let surveyAns = 0; surveyAns < surveyArr.length; surveyAns++) {
       this.store
        .collection('survey-answers')
        .doc(this.fireUserId)
        .collection('survey')
        .doc(surveyCompletionInfo.uuid)
        .collection('answers')
        .doc(surveyArr[surveyAns].qUUID)
        .set(surveyArr[surveyAns]);
    }
    return this.getSurveyAnswers(surveyCompletionInfo.uuid);
  }

  saveSurveyAnswer(
    surveyUUID: string,
    surveyAnswer: SurveyAnswer,
    sequence: number,
    percentage: number
  ) {
    const date = this.hs.getLocalZoneISOString(new Date());
    this.store
      .collection('survey-answers')
      .doc(this.fireUserId)
      .collection('survey')
      .doc(surveyUUID)
      .set({
        isComplete: false,
        uuid: surveyUUID,
        sequence: sequence,
        datetime: date,
        completedPercentage: percentage,
      });

    return this.store
      .collection('survey-answers')
      .doc(this.fireUserId)
      .collection('survey')
      .doc(surveyUUID)
      .collection('answers')
      .doc(surveyAnswer.qUUID)
      .set(surveyAnswer);
  }

  getSurveyAnswers(surveyUUID: string): Observable<SurveyAnswer[]> {
    return this.store
      .collection('survey-answers')
      .doc(this.fireUserId)
      .collection('survey')
      .doc(surveyUUID)
      .collection<SurveyAnswer>('answers')
      .valueChanges();
  }

  getSurveyAnsweredInfo(surveyUUID: string): Observable<any> {
    return this.store
      .collection('survey-answers')
      .doc(this.fireUserId)
      .collection('survey')
      .doc(surveyUUID)
      .valueChanges()
      .pipe(take(1));
  }

  async deleteSurveyAnswers(surveyUUID: string) {
    const qry = await this.store
      .collection('survey-answers')
      .doc(this.fireUserId)
      .collection('survey')
      .doc(surveyUUID)
      .collection('answers')
      .ref.get();

    qry.forEach((doc) => {
      doc.ref.delete();
    });
    return this.store
      .collection('survey-answers')
      .doc(this.fireUserId)
      .collection('survey')
      .doc(surveyUUID)
      .delete();
  }

  public getAuthUser(cb): void {
    this.afAuth.authState.subscribe(
      (authUser: AuthUser) => {
        if (authUser) {
          this.fireUserId = authUser.uid;
          cb();
        }
      },
      () => {
        this.fireUserId = null;
      }
    );
  }
  
  public registerBasic(user: UserProfile) {
    return this.store
      .collection<UserProfile>('user')
      .doc(this.fireUserId)
      .update(user);
  }

  public registerAdditional(user: UserProfile) {
    return this.store
      .collection<UserProfile>('user')
      .doc(this.fireUserId)
      .update(user);
  }

  public updateLastLoginTime() {
    return this.store
      .collection<UserProfile>('user')
      .doc(this.fireUserId)
      .update({ lastLogin: this.date });
  }

  public getProfileInfo(): Observable<UserProfile> {
    return this.store
      .collection('user')
      .doc<UserProfile>(this.fireUserId)
      .valueChanges();
  }

  public getActivityInfo(): Observable<DefaultActivity> {
    return this.store
      .collection('my-default-activities')
      .doc<DefaultActivity>(this.fireUserId)
      .valueChanges();
  }

  public addProfilePicture(Image) {
    return this.store
      .collection('user')
      .doc(this.fireUserId)
      .update({ photoURL: Image });
  }

  public getLoginUser(uid: string): Observable<UserProfile> {
    return this.store.collection('user').doc<UserProfile>(uid).valueChanges();
    // .pipe(take(1));
  }

  public addDefaultActivities(defaultActivity) {
    return this.store
      .collection('my-default-activities')
      .doc(this.fireUserId)
      .collection('activities')
      .doc(defaultActivity.uuid)
      .set(defaultActivity);
  }

  public updateMyDefaultActivity(myDefaultActivity) {
    return this.store
      .collection('my-default-activities')
      .doc(this.fireUserId)
      .collection('activities')
      .doc(myDefaultActivity.uuid)
      .update(myDefaultActivity);
  }

  public addMyActivity(activity: MyActivity) {
    return this.store
      .collection('my-activities')
      .doc(this.fireUserId)
      .collection('activities')
      .doc(activity.uuid)
      .set(activity);
  }

  public editMyActivity(activity: MyActivity) {
    return this.store
      .collection('my-activities')
      .doc(this.fireUserId)
      .collection('activities')
      .doc(activity.uuid)
      .update(activity);
  }

  public editMyDefaultActivity(myDefaultActivity: DefaultActivity) {
    return this.store
      .collection('my-default-activities')
      .doc(this.fireUserId)
      .collection('activities')
      .doc(myDefaultActivity.uuid)
      .update(myDefaultActivity);
  }

  public deleteMyDefaultActivity(uuid: string) {
    return this.store
      .collection('my-default-activities')
      .doc(this.fireUserId)
      .collection('activities')
      .doc(uuid)
      .delete();
  }

  public deleteMyActivity(uuid: string) {
    return this.store
      .collection('my-activities')
      .doc(this.fireUserId)
      .collection('activities')
      .doc(uuid)
      .delete();
  }

  public getDefaultActivities(): Observable<DefaultActivity[]> {
    return this.store
      .collection<DefaultActivity>('default-activities')
      .valueChanges();
  }

  public getMyActivities(): Observable<MyActivity[]> {
    return this.store
      .collection('my-activities')
      .doc(this.fireUserId)
      .collection<MyActivity>('activities')
      .valueChanges();
  }

  public getMyDefaultActivities(): Observable<MyDefaultActivity[]> {
    return this.store
      .collection('my-default-activities')
      .doc(this.fireUserId)
      .collection<MyDefaultActivity>('activities')
      .valueChanges();
  }

  public getMyDailyActivities(date: string): Observable<MyDailyValue> {
    return this.store
      .collection('my-daily-activities')
      .doc(this.fireUserId)
      .collection('values')
      .doc<MyDailyValue>(date)
      .valueChanges();
  }
  
  public saveWellBeingValues(MyDailyValue) {
    return this.store
      .collection('my-daily-activities')
      .doc(this.fireUserId)
      .collection('values')
      .doc(MyDailyValue.date)
      .set(MyDailyValue);
  }

  public addDailyActivity(dailyActivites) {
    return this.store
      .collection('my-daily-activities')
      .doc(this.fireUserId)
      .collection<MyDailyValue>('values')
      .doc(dailyActivites.date)
      .set(dailyActivites);
  }

  public getSurveys() {
    return this.store.collection<UserSurveyInfo>('surveys').valueChanges();
  }
  public getUUID(): string {
    return this.store.createId();
  }
  public getSurveyQuestions(surveyUUID: string) {
    return this.store
      .collection('surveys')
      .doc(surveyUUID)
      .collection<SurveyQuestion>('questions')
      .valueChanges()
      .pipe(take(1));
  }

  // Store user in db
  public SetUserData(user) {
    return this.store.collection('user').doc(user.uid).set({
      email: user.email,
      // firstname: name,
    });
  }

  // Store user in db
  public SetUserDataAfterGoogleFbLogin(user) {
    var name = user.displayName.split(' ');
    return this.store.collection('user').doc(user.uid).set({
      email: user.email,
      firstname: name[0],
      lastname: name[1],
      photoURL: user.photoURL,
    });
  }
  // these three method add multiple data
  public addFitbitUser(user) {
    return this.store.collection('fitbit').doc(this.fireUserId).set(user);
  }

  public updateFitbitUser(user) {
    return this.store.collection('fitbit').doc(this.fireUserId).update(user);
  }

  public updateLastSyncDate() {
    const date = this.hs.getLocalZoneISOString(new Date());
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .update({ lastSyncData: date });
  }

  public updateFitbitCredential(userid: string, accesstoken: string) {
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .update({ userId: userid, accessToken: accesstoken, isActive: true });
  }

  public removeAccount() {
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .update({ isActive: false });
  }

  public disconnectApple() {
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .update({ isActive: false });
  }

  public addFitbitHeart(heart, date: string) {
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .collection('heart')
      .doc(date)
      .set(heart);
  }

  public addFitbitHeartIntraday(heart, date: string) {
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .collection('heart-intraday')
      .doc(date)
      .set(heart);
  }

  public getSleepIntraday(date: string) {
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .collection('heart-intraday')
      .doc<SleepIntraday>(date)
      .valueChanges();
  }

  public addFitbitSleep(sleep, date: string) {
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .collection('sleep')
      .doc(date)
      .set(sleep);
  }

  public addSleepManually(date, newSleepDates) {
    return this.store
      .collection('sleep-manually')
      .doc(this.fireUserId)
      .collection('values')
      .doc(date)
      .set({ newSleepDates, date: date });
  }

  public getFitbitSleep(date: string) {
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .collection('sleep')
      .doc(date)
      .valueChanges()
      .pipe(take(1));
  }

  public getFitbitHeartData(date: string) {
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .collection('heart')
      .doc(date)
      .valueChanges()
      .pipe(take(1));
  }

  public checkFitbitSleep() {
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .collection('sleep')
      .valueChanges()
      .pipe(take(1));
  }

  public checkFitbitHeartData() {
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .collection('heart')
      .valueChanges()
      .pipe(take(1));
  }

  public getFitbitUser() {
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .valueChanges()
      .pipe(take(1));
  }

  public isCompleted(surveyUUID: string) {
    const date = this.hs.getLocalZoneISOString(new Date());
    return this.store
      .collection('survey-answers')
      .doc(this.fireUserId)
      .collection('survey')
      .doc(surveyUUID)
      .update({ isComplete: true, uuid: surveyUUID, datetime: date });
  }
  public myAnsweredSurveys() {
    return this.store
      .collection('survey-answers')
      .doc(this.fireUserId)
      .collection('survey')
      .valueChanges();
  }

  // Ios HealthKit Functions

  addIosSleepData(sleep, date: string) {
    sleep.date = date;
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .collection('sleep')
      .doc(date)
      .set(sleep);
  }

  addIosHRVData(hrv, date: string) {
    hrv.date = date;
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .collection('hrv')
      .doc(date)
      .set(hrv);
  }

  addIosHeartRateData(heartrate, date: string) {
    heartrate.date = date;
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .collection('heartRate')
      .doc(date)
      .set(heartrate);
  }

  addIosstepCount(stepCount, date: string) {
    stepCount.date = date;
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .collection('stepCount')
      .doc(date)
      .set(stepCount);
  }


  getHealthKitSleepData(date: string): Promise<any> {
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .collection('sleep')
      .doc(date)
      .valueChanges()
      .pipe(take(1))
      .toPromise();
  }

  getHealthKitStepData(date: string): Promise<any> {
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .collection('stepCount')
      .doc(date)
      .valueChanges()
      .pipe(take(1))
      .toPromise();
  }

  getIosHRVData(date: string): Promise<any> {
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .collection('hrv')
      .doc(date)
      .valueChanges()
      .pipe(take(1))
      .toPromise();
  }

  public checkHealthkitSleep() {
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .collection('sleep')
      .valueChanges();
  }

  public checkHealthkitHRVData() {
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .collection('hrv')
      .valueChanges();
  }

  public updateHealthKitLastSyncDate() {
    const date = this.hs.getLocalZoneISOString(new Date());
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .set({ lastSyncData: date, isActive: true });
  }

  public getHealthKitUser() {
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .valueChanges()
      .pipe(take(1));
  }

  public getMyWeeklyActivities(
    startDate: string,
    endDate: string
  ): Observable<MyDailyValue[]> {
    return this.store
      .collection('my-daily-activities')
      .doc(this.fireUserId)
      .collection<MyDailyValue>('values', (ref) =>
        ref.orderBy('date').startAt(startDate).endAt(endDate)
      )
      .valueChanges();
  }

  getWeeklyMyActivity(uuid: string) {
    return this.store
      .collection('my-activities')
      .doc(this.fireUserId)
      .collection('activities')
      .doc(uuid)
      .valueChanges();
  }

  public getFitbitWeeklySleep(startDate: string, endDate: string) {
    return this.store
      .collection('fitbit')
      .doc(this.fireUserId)
      .collection('sleep', (ref) =>
        ref.orderBy('dateOfSleep').startAt(startDate).endAt(endDate)
      )
      .valueChanges();
  }
  
  public getAppleHealthKitWeeklySleep(startDate: string, endDate: string) {
    return this.store
      .collection('healthkit')
      .doc(this.fireUserId)
      .collection('sleep', (ref) =>
        ref.orderBy('date').startAt(startDate).endAt(endDate)
      )
      .valueChanges();
  }

  public getWeelySleepDataManually(startDate, endDate) {
    return this.store
      .collection('sleep-manually')
      .doc(this.fireUserId)
      .collection('values', (ref) =>
        ref.orderBy('date').startAt(startDate).endAt(endDate)
      )
      .valueChanges();
  }

  getSleepManully(date: string) {
    return this.store
      .collection('sleep-manually')
      .doc(this.fireUserId)
      .collection('values')
      .doc(date)
      .valueChanges()
      .pipe(take(1));
  }

  notificationSettings(value) {
    return this.store.collection('settings').doc(this.fireUserId).set(value);
  }

  updateNotificationSettings(value) {
    return this.store.collection('settings').doc(this.fireUserId).update(value);
  }

  getNotificationSettings() {
    return this.store
      .collection('settings')
      .doc(this.fireUserId)
      .valueChanges();
  }

  deleteMannualSleepTime(date: string) {
    return this.store
      .collection('sleep-manually')
      .doc(this.fireUserId)
      .collection('values')
      .doc(date)
      .delete();
  }

  savePSQLResult(psqi) {
    return this.store
      .collection('survey-answers')
      .doc(this.fireUserId)
      .collection('survey')
      .doc('dHPMFMoyuCq6OxZCyI1m')
      .update(psqi);
  }

  getpittsburghSleepQualityIndex(userId?: string) {
    const appliedUserId: string = userId || this.fireUserId;
    return this.store
      .collection('survey-answers')
      .doc(this.fireUserId)
      .collection('survey')
      .doc('dHPMFMoyuCq6OxZCyI1m')
      .valueChanges()
      .pipe(take(1));
  }
}
